/**
 * Public Marketplace Module Container - Story 7.1: Create Marketplace Homepage
 * Routes and layout for PUBLIC marketplace functionality (no authentication required)
 *
 * This container handles all public-facing marketplace routes:
 * - Homepage with hero, categories, and featured products
 * - B2B Marketplace with product catalog (Havan Logs, Funeral Logs, Fertilizers)
 * - Product listings (search, filter, browse)
 * - Product detail pages
 * - Public inquiry submission
 *
 * Note: This is separate from Marketplace.container.tsx which handles
 * AUTHENTICATED seller dashboard routes.
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MarketplaceHomePage } from './pages/MarketplaceHomePage';
import { B2BMarketplacePage } from './pages/B2BMarketplacePage';
import { ProductBrowsePage } from './pages/ProductBrowsePage';
import { SearchResultsPage } from './pages/SearchResultsPage';
import { ProductDetailPage } from './components/ProductDetailPage';
import { InquiryConfirmation } from './components/InquiryConfirmation';

/**
 * PublicMarketplaceModule Component
 * Routes for public marketplace functionality
 *
 * Routes:
 * - /public/marketplace - B2B Marketplace with Havan Logs, Funeral Logs, Fertilizers (NEW)
 * - /public/marketplace/home - Original landing page (Story 7.1)
 * - /marketplace/products - Product listings with filters, search, sort (Story 7.2)
 * - /marketplace/search - Product search results page (Story 7.5)
 * - /marketplace/products/:id - Product details (Story 7.3)
 * - /marketplace/inquiry/confirmation - Inquiry submission confirmation (Story 7.4)
 */
const PublicMarketplaceModule: React.FC = () => {
  return (
    <Routes>
      {/* B2B Marketplace - Default route with Havan Logs, Funeral Logs, Fertilizers */}
      <Route index element={<B2BMarketplacePage />} />

      {/* Original Homepage - accessible via /home */}
      <Route path="/home" element={<MarketplaceHomePage />} />
      <Route path="/original" element={<MarketplaceHomePage />} />

      {/* Product Listing - Story 7.2: Create Product Browse/Listing Page */}
      <Route path="/products" element={<ProductBrowsePage />} />

      {/* Product Search Results - Story 7.5: Implement Product Search */}
      <Route path="/search" element={<SearchResultsPage />} />

      {/* Product Detail - Story 7.3: Create Product Detail Page */}
      <Route path="/products/:externalId" element={<ProductDetailPage />} />

      {/* Inquiry Confirmation - Story 7.4: Create Inquiry Submission Flow */}
      <Route path="/inquiry/confirmation" element={<InquiryConfirmation />} />

      {/* Fallback - redirect to B2B marketplace */}
      <Route path="*" element={<Navigate to="/public/marketplace" replace />} />
    </Routes>
  );
};

export default PublicMarketplaceModule;
